import Constant from '@/common/constant/Constant';

/**
 * translationStore - the synchronous hot tier of the translation cache.
 *
 * tr() runs inside render, so its lookup has to be synchronous. IndexedDB is not,
 * so the durable tier (translationDb) hydrates THIS module asynchronously and
 * tr() only ever reads from here.
 *
 * Deliberately plain module state - no React, no Recoil, no IndexedDB:
 *   - a Map, so adding k translations is O(k) instead of the O(n) object spread
 *     that copying the whole dictionary into a Recoil atom used to cost;
 *   - read straight from tr(), so a render pass with 65 tr() calls does 65 Map
 *     lookups instead of 65 Recoil snapshot reads.
 *
 * Recoil keeps only an integer (translationVersionAtom) as the re-render signal;
 * `notify` is how this module tells the loader to bump it.
 */

// NUL as an escape, not a literal byte: it cannot appear in displayable text
// (so a key can never be forged) while the source stays plain ASCII.
const KEY_SEPARATOR = String.fromCharCode(0);

const DEFAULT_CONFIG = {
    enabled: false,
    mainLanguage: Constant.TRANSLATION_SOURCE_LANGUAGE,
    sourceLanguage: Constant.TRANSLATION_SOURCE_LANGUAGE,
    dictionaryOrgId: null
};

let config = { ...DEFAULT_CONFIG };
let entries = new Map();
let version = 0;
let notify = null;

// Strings already sent to the backend this session. A string the engine cannot
// translate stays a MISS forever, so without this it would be requested on every
// flush. Session-wide on purpose: not cleared by reset()/clearEntries().
const requested = new Set();

const bump = () => {
    version += 1;
    if (typeof notify === 'function') {
        notify();
    }
};

/**
 * `usage` is part of the key so AUTO / FIELD_LABEL / TAG / ONDEMAND can share one
 * store without colliding once the feature grows past fixed UI text.
 */
export const buildCacheKey = (usage, sourceText) => `${usage}${KEY_SEPARATOR}${sourceText}`;

export const getConfig = () => config;

/** Replaces the language config. Bumps the version so the UI repaints. */
export const setConfig = nextConfig => {
    config = { ...DEFAULT_CONFIG, ...nextConfig };
    bump();
};

/** Synchronous lookup - this is what tr() calls. */
export const get = (usage, sourceText) => entries.get(buildCacheKey(usage, sourceText));

export const has = (usage, sourceText) => entries.has(buildCacheKey(usage, sourceText));

/**
 * Adds translations for one usage.
 * @param {string} usage
 * @param {Object} pairs - { [sourceText]: translatedText }
 * @returns {number} how many entries were actually stored.
 */
export const putMany = (usage, pairs) => {
    if (!pairs || typeof pairs !== 'object') {
        return 0;
    }

    let added = 0;
    Object.keys(pairs).forEach(sourceText => {
        const translatedText = pairs[sourceText];
        // An empty translation is not a translation - storing it would turn a
        // recoverable MISS into a permanent blank.
        if (!sourceText || !translatedText) return;
        entries.set(buildCacheKey(usage, sourceText), translatedText);
        added += 1;
    });

    if (added > 0) {
        bump();
    }
    return added;
};

/**
 * Loads rows as they come back from translationDb.readScope().
 * @param {Array<{usage: string, sourceText: string, translatedText: string}>} rows
 * @returns {number} how many entries were actually stored.
 */
export const loadRows = rows => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return 0;
    }

    let added = 0;
    rows.forEach(row => {
        if (!row?.usage || !row?.sourceText || !row?.translatedText) return;
        entries.set(buildCacheKey(row.usage, row.sourceText), row.translatedText);
        added += 1;
    });

    if (added > 0) {
        bump();
    }
    return added;
};

/**
 * Drops translations for one usage, e.g. after the entry was deleted on 翻訳管理.
 * @returns {number} how many entries were actually removed.
 */
export const removeMany = (usage, sourceTexts) => {
    if (!Array.isArray(sourceTexts) || sourceTexts.length === 0) {
        return 0;
    }

    let removed = 0;
    sourceTexts.forEach(sourceText => {
        const key = buildCacheKey(usage, sourceText);
        // Forget the request too, so the string is translated again in this session.
        requested.delete(key);
        if (entries.delete(key)) {
            removed += 1;
        }
    });

    if (removed > 0) {
        bump();
    }
    return removed;
};

/** Drops every cached translation. Used when translation goes OFF or the org changes. */
export const reset = () => {
    entries = new Map();
    config = { ...DEFAULT_CONFIG };
    bump();
};

/** Drops the translations but keeps the config - used when only the scope changes. */
export const clearEntries = () => {
    entries = new Map();
    bump();
};

export const getVersion = () => version;

export const isRequested = (usage, sourceText) => requested.has(buildCacheKey(usage, sourceText));

export const markRequested = (usage, sourceText) => {
    requested.add(buildCacheKey(usage, sourceText));
};

export const requestedCount = () => requested.size;

export const size = () => entries.size;

/**
 * Registers the callback that turns a store mutation into a re-render.
 * The loader sets this on mount and clears it (null) on unmount.
 */
export const setNotifier = fn => {
    notify = typeof fn === 'function' ? fn : null;
};

const translationStore = {
    buildCacheKey,
    getConfig,
    setConfig,
    get,
    has,
    putMany,
    loadRows,
    removeMany,
    reset,
    clearEntries,
    getVersion,
    isRequested,
    markRequested,
    requestedCount,
    size,
    setNotifier
};

export default translationStore;
